import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Button,
  Modal,
  TextField,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import { FaPlus, FaTrash, FaEye, FaCode, FaEdit } from 'react-icons/fa';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import JSZip from 'jszip';
import config from '../config';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const server_URL = config.SERVER_URL;
const ZIP_IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.svg', '.webp', '.bmp'];

const defaultTemplateForm = {
  id: null,
  name: '',
  sourceType: 'custom',
  html: '',
  selectedBlocks: [],
  fileName: '',
  zipFile: null,
  zipFileName: '',
  zipFilePath: '',
  imageMap: {}, // Maps image paths to blob URLs
  editorMode: 'html', // 'html' or 'ckeditor'
  customer_id: '', // New: customer selection
};

const defaultBlockForm = {
  id: null,
  name: '',
  category: 'General',
  html: '',
};

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
  width: '95%',
  maxWidth: 1200,
  maxHeight: '90vh',
  overflowY: 'auto',
};

const Template = () => {
  const [templates, setTemplates] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [blockModalOpen, setBlockModalOpen] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [activePreview, setActivePreview] = useState(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [formPreviewHtml, setFormPreviewHtml] = useState('');
  const [templateForm, setTemplateForm] = useState(defaultTemplateForm);
  const [blockForm, setBlockForm] = useState(defaultBlockForm);

  // Additional state for customer management
  const [customers, setCustomers] = useState([]);
  const [filterCustomers, setFilterCustomers] = useState([]);
  const [customerFilter, setCustomerFilter] = useState('');
  const [userRole, setUserRole] = useState(null);
  const [loggedUserId, setLoggedUserId] = useState(null);
  const [customFields, setCustomFields] = useState([]); // Custom fields for selected customer

  // Initialize user role and ID on mount
  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUserRole(userData.role_id || null);
    setLoggedUserId(userData.id || null);
    // Load customers on mount for use in add/edit modals
    fetchCustomers();
  }, []);

  const zipFileInputRef = useRef(null);
  const editorRef = useRef(null);

  // Fetch user info and customers on mount (for role logic)
  // useEffect(() => {
  //   // Get user info from localStorage
  //   const userData = JSON.parse(localStorage.getItem('user') || '{}');
  //   setUserRole(userData.role_id || null);
  //   setLoggedUserId(userData.id || null);

  //   fetchCustomers(userData);
  // }, []);

  // Fetch customer accounts
  const fetchCustomers = async (userDataArg) => {
    try {
      const userData = userDataArg || JSON.parse(localStorage.getItem('user') || '{}');
      const response = await fetch(server_URL + '/api/customers', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          method: 'list',
          user_id: userData.id || null,
          role_id: userData.role_id || null
        }),
      });
      if (!response.ok) throw new Error('Failed to fetch customers');

      const data = await response.json();
      if (data.status === '1' && data.data) {
        setCustomers(data.data);
        // For filter dropdown: role 1 sees all, role 2 sees only logged customer
        if (userData.role_id === 1) {
          setFilterCustomers(data.data);
        } else if (userData.role_id === 2 && userData.id) {
          setFilterCustomers(data.data.filter(customer => customer.id === userData.id));
        }
        return data.data; // Return customers for use in calling functions
      } else {
        setCustomers([]);
        setFilterCustomers([]);
        return [];
      }
    } catch (error) {
      setCustomers([]);
      console.error('Error fetching customers:', error);
      toast.error('Unable to load customer list');
      return [];
    }
  };

  const fetchCustomFieldsForCustomer = async (customerId) => {
    if (!customerId || customerId === 0) {
      setCustomFields([]);
      return;
    }
    
    try {
      const response = await fetch(server_URL + '/api/trigger_automations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'get_custom_fields',
          customer_id: customerId,
        }),
      });

      const data = await response.json();
      if (data.status === '1' && data.data) {
        setCustomFields(data.data || []);
      } else {
        setCustomFields([]);
      }
    } catch (error) {
      console.error('Error fetching custom fields:', error);
      setCustomFields([]);
    }
  };

  const fetchTemplatesFromServer = useCallback(async (filterCustomerId = null) => {
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const requestBody = {
        method: 'list',
        user_id: userData.id || null,
        role_id: userData.role_id || null,
      };
      
      // Priority: explicit customer filter > role-based filtering
      // If role is 2, filter by logged user's customer_id (unless explicit filter is provided)
      if (filterCustomerId !== null && filterCustomerId !== '') {
        const customerIdValue = String(filterCustomerId) === '0' ? 0 : parseInt(filterCustomerId, 10);
        requestBody.customer_id = customerIdValue;
        console.log('Filtering by customer_id:', customerIdValue);
      } else if (userData.role_id === 2 && userData.id) {
        // For role 2 users, automatically filter by their own customer_id
        requestBody.customer_id = userData.id;
        console.log('Role 2 user - filtering by own customer_id:', userData.id);
      }
      
      console.log('Request body:', requestBody);
      
      const response = await fetch(`${server_URL}/api/templates`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch templates from server');
      }

      const data = await response.json();
      if (data.status === '1' && Array.isArray(data.data)) {
        setTemplates(data.data);
      } else {
        setTemplates([]);
      }
    } catch (error) {
      console.error('Error fetching templates from server:', error);
      toast.error('Unable to load templates from server');
    }
  }, []);

  useEffect(() => {
    fetchTemplatesFromServer();
  }, [fetchTemplatesFromServer]);

  const handleFilter = () => {
    fetchTemplatesFromServer(customerFilter);
  };

  const handleClearFilter = () => {
    setCustomerFilter('');
    // For role 2 users, fetchTemplatesFromServer will automatically filter by their own customer_id
    fetchTemplatesFromServer();
  };

  const blockOptions = useMemo(
    () => blocks.map((block) => ({ label: block.name, value: block.id, category: block.category })),
    [blocks]
  );

  const handleTemplateFormChange = (field, value) => {
    setTemplateForm((prev) => {
      const updated = { ...prev, [field]: value };

      if (field === 'zipFile') {
        updated.zipFilePath = '';
      }
      
      // If customer_id changes, fetch custom fields for that customer
      if (field === 'customer_id') {
        if (value && value !== 0) {
          fetchCustomFieldsForCustomer(value);
        } else {
          setCustomFields([]);
        }
      }
      
      // Update form preview HTML when HTML content or image map changes
      if (field === 'html' || field === 'imageMap' || field === 'zipFile') {
        const htmlToPreview = field === 'html' ? value : updated.html;
        const imageMapToUse = field === 'imageMap' || field === 'zipFile' ? updated.imageMap : prev.imageMap;
        const processedHtml = processHtmlWithImages(htmlToPreview, imageMapToUse);
        setFormPreviewHtml(processedHtml);
      }
      
      return updated;
    });
  };

  const handleBlockFormChange = (field, value) => {
    setBlockForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleHtmlFile = (file) => {
    if (!file) {
      handleTemplateFormChange('html', '');
      handleTemplateFormChange('fileName', '');
      setFormPreviewHtml('');
      // Clear CKEditor if active
      if (editorRef.current) {
        editorRef.current.setData('');
      }
      return;
    }
    const reader = new FileReader();
    reader.onload = (event) => {
      const htmlContent = event.target.result || '';
      handleTemplateFormChange('html', htmlContent);
      handleTemplateFormChange('fileName', file.name);
      // Update CKEditor if active
      if (editorRef.current) {
        editorRef.current.setData(htmlContent);
      }
      // Update preview with new HTML and existing images
      const processedHtml = processHtmlWithImages(htmlContent, templateForm.imageMap);
      setFormPreviewHtml(processedHtml);
    };
    reader.onerror = () => toast.error('Could not read the HTML file.');
    reader.readAsText(file);
  };

  const handleZipFile = async (file) => {
    if (!file) {
      handleTemplateFormChange('zipFile', null);
      handleTemplateFormChange('zipFileName', '');
      handleTemplateFormChange('imageMap', {});
      return;
    }

    if (!file.name.toLowerCase().endsWith('.zip')) {
      toast.error('Please upload a ZIP file');
      return;
    }

    try {
      const zip = new JSZip();
      const zipData = await zip.loadAsync(file);
      const imageMap = {};
      const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.svg', '.webp', '.bmp'];

      // Extract all image files from ZIP
      for (const [fileName, zipEntry] of Object.entries(zipData.files)) {
        if (zipEntry.dir) continue;
        
        const lowerFileName = fileName.toLowerCase();
        const isImage = imageExtensions.some(ext => lowerFileName.endsWith(ext));
        
        if (isImage) {
          const blob = await zipEntry.async('blob');
          const blobUrl = URL.createObjectURL(blob);
          // Store with both full path and just filename for flexible matching
          imageMap[fileName] = blobUrl;
          const justFileName = fileName.split('/').pop();
          if (justFileName !== fileName) {
            imageMap[justFileName] = blobUrl;
          }
        }
      }

      handleTemplateFormChange('zipFile', file);
      handleTemplateFormChange('zipFileName', file.name);
      handleTemplateFormChange('imageMap', imageMap);
      
      // Update form preview with new images
      const processedHtml = processHtmlWithImages(templateForm.html, imageMap);
      setFormPreviewHtml(processedHtml);
      
      if (Object.keys(imageMap).length === 0) {
        toast.warning('No images found in the ZIP file');
      } else {
        toast.success(`Found ${Object.keys(imageMap).length} image(s) in ZIP`);
      }
    } catch (error) {
      console.error('Error processing ZIP file:', error);
      toast.error('Failed to process ZIP file. Please ensure it is a valid ZIP archive.');
    }
  };

  const processHtmlWithImages = (html, imageMap) => {
    if (!html || !imageMap || Object.keys(imageMap).length === 0) {
      return html;
    }

    let processedHtml = html;
    
    // Replace image sources in various formats
    // Match src="path/to/image.jpg" or src='path/to/image.jpg'
    processedHtml = processedHtml.replace(/src=["']([^"']+\.(jpg|jpeg|png|gif|svg|webp|bmp))["']/gi, (match, imagePath) => {
      // Try exact match first
      if (imageMap[imagePath]) {
        return `src="${imageMap[imagePath]}"`;
      }
      
      // Try just the filename
      const fileName = imagePath.split('/').pop();
      if (imageMap[fileName]) {
        return `src="${imageMap[fileName]}"`;
      }
      
      // Try matching any key that contains the filename
      const matchingKey = Object.keys(imageMap).find(key => 
        key.toLowerCase().includes(fileName.toLowerCase())
      );
      if (matchingKey) {
        return `src="${imageMap[matchingKey]}"`;
      }
      
      return match; // Return original if no match found
    });

    return processedHtml;
  };

  const buildHtmlFromBlocks = (selectedIds) => {
    if (!selectedIds.length) return '';
    return selectedIds
      .map((id) => blocks.find((block) => block.id === id))
      .filter(Boolean)
      .map((block) => block.html)
      .join('\n');
  };

  const resetTemplateForm = () => {
    // Only pre-select customer if only 1 and you know from state
    setTemplateForm((prev) => ({
      ...defaultTemplateForm,
      customer_id: (customers && customers.length === 1) ? customers[0].id : ''
    }));
    setFormPreviewHtml('');
  };

  const resetBlockForm = () => {
    setBlockForm(defaultBlockForm);
  };

  const openNewTemplateModal = async () => {
    // Always refresh customers on open
    const fetchedCustomers = await fetchCustomers();
    
    // Reset form and preselect customer if only one option
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    const customerId = (fetchedCustomers.length === 1) ? fetchedCustomers[0].id : (userData.role_id === 2 && userData.id ? userData.id : '');
    
    setTemplateForm({
      ...defaultTemplateForm,
      customer_id: customerId
    });
    setFormPreviewHtml('');
    
    // Fetch custom fields if customer is selected
    if (customerId && customerId !== 0) {
      await fetchCustomFieldsForCustomer(customerId);
    } else {
      setCustomFields([]);
    }
    
    setTemplateModalOpen(true);
  };

  const openEditTemplateModal = async (template) => {
    // Ensure customers are loaded before opening edit modal
    await fetchCustomers();
    
    // Find the matching customer id if present
    const customerId = template.customer_id !== undefined && template.customer_id !== null ? template.customer_id : '';
    
    // Load images from saved zipFilePath if available
    let imageMap = {};
    if (template.zipFilePath) {
      try {
        // Fetch image files from the server path
        const imagePath = template.zipFilePath;
        // Extract template ID from path (e.g., "http://host/static/images/templates/123/" -> "123")
        const templateIdMatch = imagePath.match(/templates\/(\d+)\//);
        if (templateIdMatch) {
          const templateId = templateIdMatch[1];
          // List images in the directory (we'll need to fetch them)
          // For now, we'll update the HTML to use the server path directly
          // The images should already be accessible via the zipFilePath URL
        }
        // Create image map using server URLs
        // Extract image filenames from HTML
        const html = template.html || '';
        // Ensure zipFilePath ends with a single slash
        const basePath = template.zipFilePath.endsWith('/') 
          ? template.zipFilePath 
          : `${template.zipFilePath}/`;
        
        const imageRegex = /src=["']([^"']+\.(jpg|jpeg|png|gif|svg|webp|bmp))["']/gi;
        let match;
        while ((match = imageRegex.exec(html)) !== null) {
          const imagePath = match[1];
          // If it's a relative path, prepend the zipFilePath
          if (!imagePath.startsWith('http') && !imagePath.startsWith('blob:')) {
            // Preserve the relative path structure (e.g., "myImg/image.jpg" or "image.jpg")
            // Remove leading slash if present
            const cleanPath = imagePath.startsWith('/') ? imagePath.substring(1) : imagePath;
            const fullUrl = `${basePath}${cleanPath}`;
            
            // Store multiple mappings for flexible matching
            const fileName = cleanPath.split('/').pop(); // Just the filename
            imageMap[fileName] = fullUrl;
            imageMap[imagePath] = fullUrl;
            imageMap[cleanPath] = fullUrl;
            // Also store with leading slash variants
            imageMap[`/${cleanPath}`] = fullUrl;
            imageMap[`/${imagePath}`] = fullUrl;
          } else if (imagePath.includes(basePath)) {
            // Already has full URL, extract relative path from basePath
            const relativePath = imagePath.replace(basePath, '');
            const fileName = relativePath.split('/').pop();
            imageMap[fileName] = imagePath;
            imageMap[relativePath] = imagePath;
          }
        }
      } catch (error) {
        console.error('Error loading images from zipFilePath:', error);
      }
    }
    
    const formData = {
      id: template.id,
      name: template.name || '',
      sourceType: template.sourceType || 'custom',
      html: template.html || '',
      selectedBlocks: template.selectedBlocks || [],
      fileName: template.fileName || '',
      zipFile: null, // Can't restore File object, user needs to re-upload if needed
      zipFileName: template.zipFileName || '',
      zipFilePath: template.zipFilePath || '',
      imageMap: imageMap, // Loaded from saved zipFilePath
      editorMode: template.editorMode || 'html',
      customer_id: customerId, // Ensure customer_id is properly set
    };
    setTemplateForm(formData);
    
    // Process HTML with loaded images
    const processedHtml = processHtmlWithImages(template.html || '', imageMap);
    setFormPreviewHtml(processedHtml);
    
    // Fetch custom fields if customer is selected
    if (customerId && customerId !== 0) {
      await fetchCustomFieldsForCustomer(customerId);
    } else {
      setCustomFields([]);
    }
    
    setTemplateModalOpen(true);
  };

  const handleEditorReady = (editor) => {
    editorRef.current = editor;
    // Set initial data if switching to CKEditor
    if (templateForm.html) {
      editor.setData(templateForm.html);
    }
  };

  const openNewBlockModal = () => {
    resetBlockForm();
    setBlockModalOpen(true);
  };

  const handleTemplateSubmit = (event) => {
    event.preventDefault();
    // Validate customer selection
    // Allow 0 for "Common (No Customer)" for role 1 users, or any valid customer ID
    const customerId = templateForm.customer_id;
    if (customerId === '' || customerId === null || customerId === undefined) {
      toast.error('Please select a customer');
      return;
    }
    if (!templateForm.name.trim()) {
      toast.error('Template name is required');
      return;
    }

    let finalHtml = templateForm.html.trim();
    if (templateForm.sourceType === 'blocks') {
      if (!templateForm.selectedBlocks.length) {
        toast.error('Select at least one block');
        return;
      }
      finalHtml = buildHtmlFromBlocks(templateForm.selectedBlocks);
    } else if (!finalHtml) {
      toast.error('Provide HTML content or upload a file');
      return;
    }

    const now = new Date().toISOString();
    // If editing and no new ZIP uploaded, preserve existing ZIP
    let existingZipPath = null;
    if (templateForm.id && !templateForm.zipFile) {
      const existingTemplate = templates.find(tpl => tpl.id === templateForm.id);
      if (existingTemplate?.zipFilePath) {
        existingZipPath = existingTemplate.zipFilePath;
      }
    }

    const payload = {
      id: templateForm.id || Date.now(),
      name: templateForm.name.trim(),
      sourceType: templateForm.sourceType,
      html: finalHtml,
      selectedBlocks: templateForm.selectedBlocks,
      fileName: templateForm.fileName,
      zipFileName: templateForm.zipFileName || (templateForm.id ? templates.find(tpl => tpl.id === templateForm.id)?.zipFileName : ''),
      hasZipFile: !!templateForm.zipFile || !!existingZipPath,
      editorMode: templateForm.editorMode || 'html',
      zipFilePath: templateForm.zipFilePath || existingZipPath || '',
      updatedAt: now,
      customer_id: templateForm.customer_id, // Attach customer
    };

    // Store ZIP file separately if present (as base64)
    const saveToServer = async (templatePayload, zipFileBase64Override) => {
      try {
        const userData = JSON.parse(localStorage.getItem('user') || '{}');
        const isUpdate = !!templateForm.id;
        // Send to API exactly like list.js
        const requestBody = {
          method: isUpdate ? 'update' : 'add',
          id: isUpdate ? templatePayload.id : undefined,
          name: templatePayload.name,
          sourceType: templatePayload.sourceType,
          editorMode: templatePayload.editorMode,
          html: templatePayload.html,
          selectedBlocks: templatePayload.selectedBlocks,
          fileName: templatePayload.fileName,
          zipFileName: templatePayload.zipFileName,
          user_id: userData.id || null,
          customer_id: templatePayload.customer_id,   // NEW: Attach customer id field
          is_subuser: userData.is_subuser || false,
          subuser_id: userData.subuser_id || null,
        };

        if (zipFileBase64Override) {
          requestBody.zipFileBase64 = zipFileBase64Override;
        }

        const response = await fetch(`${server_URL}/api/templates`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        });

        const data = await response.json();
        if (data.status !== '1') {
          toast.error(data.error || 'Failed to save template to server');
        } else if (!isUpdate && data.id) {
          templatePayload.id = data.id;
        }

        if (data.status === '1' && data.zipFilePath) {
          templatePayload.zipFilePath = data.zipFilePath;
        }

        if (data.status === '1') {
          fetchTemplatesFromServer();
        }
      } catch (error) {
        console.error('Error saving template to server:', error);
        toast.error('Failed to save template to server');
      }
    };

    if (templateForm.zipFile) {
      const zipReader = new FileReader();
      zipReader.onload = (e) => {
        const zipBase64 = e.target.result;
        payload.zipFileBase64 = zipBase64;
        payload.zipFilePath = '';
        
        setTemplates((prev) => {
          const exists = prev.find((tpl) => tpl.id === payload.id);
          if (exists) {
            return prev.map((tpl) => (tpl.id === payload.id ? payload : tpl));
          }
          return [...prev, payload];
        });

        // Save to API
        saveToServer(payload, zipBase64);
        
        toast.success(templateForm.id ? 'Template updated' : 'Template saved');
        setTemplateModalOpen(false);
        resetTemplateForm();
      };
      zipReader.onerror = () => {
        toast.error('Failed to read ZIP file');
      };
      zipReader.readAsDataURL(templateForm.zipFile);
      return; // Exit early, will continue in zipReader.onload
    }

    setTemplates((prev) => {
      const exists = prev.find((tpl) => tpl.id === payload.id);
      if (exists) {
        return prev.map((tpl) => (tpl.id === payload.id ? payload : tpl));
      }
      return [...prev, payload];
    });

    saveToServer(payload, null);

    toast.success(templateForm.id ? 'Template updated' : 'Template saved');
    setTemplateModalOpen(false);
    resetTemplateForm();
  };

  const handleBlockSubmit = (event) => {
    event.preventDefault();
    if (!blockForm.name.trim()) {
      toast.error('Block name is required');
      return;
    }
    if (!blockForm.html.trim()) {
      toast.error('Block HTML is required');
      return;
    }
    const payload = {
      id: blockForm.id || Date.now(),
      name: blockForm.name.trim(),
      category: blockForm.category,
      html: blockForm.html.trim(),
      updatedAt: new Date().toISOString(),
    };

    setBlocks((prev) => {
      const exists = prev.find((block) => block.id === payload.id);
      if (exists) {
        return prev.map((block) => (block.id === payload.id ? payload : block));
      }
      return [...prev, payload];
    });

    toast.success('Block saved');
    setBlockModalOpen(false);
    resetBlockForm();
  };

  const handleDeleteTemplate = async (templateId) => {
    const previousTemplates = templates;
    setTemplates((prev) => prev.filter((tpl) => tpl.id !== templateId));

    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await fetch(`${server_URL}/api/templates`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'delete',
          id: templateId,
          user_id: userData.id || null,
          is_subuser: userData.is_subuser || false,
          subuser_id: userData.subuser_id || null,
        }),
      });

      const data = await response.json();
      if (data.status === '1') {
        toast.success('Template deleted');
        fetchTemplatesFromServer();
      } else {
        toast.error(data.error || 'Failed to delete template');
        setTemplates(previousTemplates);
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Failed to delete template');
      setTemplates(previousTemplates);
    }
  };

  const handleDeleteBlock = (blockId) => {
    setBlocks((prev) => prev.filter((block) => block.id !== blockId));
    // Remove deleted block from templates referencing it
    setTemplates((prev) =>
      prev.map((tpl) => {
        if (!tpl.selectedBlocks?.length) return tpl;
        const updatedSelections = tpl.selectedBlocks.filter((id) => id !== blockId);
        return {
          ...tpl,
          selectedBlocks: updatedSelections,
          html: tpl.sourceType === 'blocks' ? buildHtmlFromBlocks(updatedSelections) : tpl.html,
        };
      })
    );
    toast.info('Block removed');
  };

  const extractImageMapFromZipBuffer = async (zipBuffer) => {
    const zip = await JSZip.loadAsync(zipBuffer);
    const imageMap = {};

    for (const [fileName, zipEntry] of Object.entries(zip.files)) {
      if (zipEntry.dir) continue;

      const lowerFileName = fileName.toLowerCase();
      const isImage = ZIP_IMAGE_EXTENSIONS.some((ext) => lowerFileName.endsWith(ext));

      if (isImage) {
        const blob = await zipEntry.async('blob');
        const blobUrl = URL.createObjectURL(blob);
        imageMap[fileName] = blobUrl;
        const justFileName = fileName.split('/').pop();
        if (justFileName !== fileName) {
          imageMap[justFileName] = blobUrl;
        }
      }
    }

    return imageMap;
  };

  const fetchZipBufferFromPath = async (zipPath) => {
    if (!zipPath) return null;
    const resolvedUrl = zipPath.startsWith('http') ? zipPath : `${server_URL}${zipPath}`;
    const response = await fetch(resolvedUrl);
    if (!response.ok) {
      throw new Error(`Failed to download ZIP from ${resolvedUrl}`);
    }
    return response.arrayBuffer();
  };

  const openPreview = async (template) => {
    setActivePreview(template);

    try {
      let imageMap = null;

      if (template.zipFileBase64) {
        // Base64 stored locally (e.g., just uploaded)
        const zipBuffer = await fetch(template.zipFileBase64).then((res) => res.arrayBuffer());
        imageMap = await extractImageMapFromZipBuffer(zipBuffer);
      } else if (template.zipFilePath) {
        const zipBuffer = await fetchZipBufferFromPath(template.zipFilePath);
        imageMap = await extractImageMapFromZipBuffer(zipBuffer);
      }

      if (imageMap) {
        const processedHtml = processHtmlWithImages(template.html, imageMap);
        setPreviewHtml(processedHtml);
      } else {
        setPreviewHtml(template.html);
      }
    } catch (error) {
      console.error('Error preparing template preview:', error);
      // toast.error('Failed to prepare template preview');
      setPreviewHtml(template.html);
    }

    setPreviewModalOpen(true);
  };

  // Cleanup blob URLs when preview closes
  useEffect(() => {
    if (!previewModalOpen) {
      // Cleanup blob URLs to prevent memory leaks
      if (previewHtml) {
        const blobUrlRegex = /src="(blob:[^"]+)"/g;
        let match;
        while ((match = blobUrlRegex.exec(previewHtml)) !== null) {
          try {
            URL.revokeObjectURL(match[1]);
          } catch (e) {
            // Ignore errors
          }
        }
      }
      setPreviewHtml('');
    }
  }, [previewModalOpen, previewHtml]);

  const templateRows = templates.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
  const blockRows = blocks.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));

  return (
    <Box sx={{ p: 3 }}>
      <ToastContainer />

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} mb={3}>
        <Box>
          <Typography variant="h4">Templates</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage reusable HTML templates and content blocks. Templates are synced to the backend for reuse across the app.
          </Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
         
          <Button variant="contained" startIcon={<FaPlus />} onClick={openNewTemplateModal}>
            Add Template
          </Button>
        </Stack>
      </Stack>

      {/* Customer Filter - Only show for role 1 (admin) */}
      {userRole === 1 && (
        <Box sx={{ 
          display: 'flex', 
          gap: 2, 
          alignItems: 'center', 
          mb: 3,
          p: 2,
          bgcolor: '#f5f5f5',
          borderRadius: 1
        }}>
          <FormControl sx={{ minWidth: 250 }}>
            <InputLabel>Filter by Customer</InputLabel>
            <Select
              value={customerFilter}
              onChange={(e) => setCustomerFilter(e.target.value)}
              label="Filter by Customer"
              size="small"
            >
              <MenuItem value="">All Customers</MenuItem>
              {/* <MenuItem value="0">Common (No Customer)</MenuItem> */}
              {filterCustomers
                .filter(customer => customer.id !== 0)
                .map((customer) => (
                  <MenuItem key={customer.id} value={customer.id}>
                    {customer.username} ({customer.email})
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <Button 
            variant="contained"
            onClick={handleFilter}
            sx={{ height: '40px' }}
          >
            Filter
          </Button>
          {customerFilter !== '' && (
            <Button 
              variant="outlined"
              color="warning"
              onClick={handleClearFilter}
              sx={{ height: '40px' }}
            >
              Clear Filter
            </Button>
          )}
          <Button 
            variant="outlined"
            onClick={() => fetchTemplatesFromServer(customerFilter)}
            sx={{ height: '40px' }}
          >
            Refresh
          </Button>
        </Box>
      )}

      <Paper elevation={1} sx={{ mb: 4 }}>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                {userRole === 1 && <TableCell>Customer</TableCell>}
                <TableCell>Updated</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {templateRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={userRole === 1 ? 4 : 3} align="center">
                    No templates added yet.
                  </TableCell>
                </TableRow>
              )}
              {templateRows.map((template) => (
                <TableRow key={template.id} hover>
                  <TableCell>
                    <Typography fontWeight={600}>{template.name}</Typography>
                  </TableCell>
                  {userRole === 1 && (
                    <TableCell>
                      {
                        template.customer_id === 0 || template.customer_id === null || template.customer_id === undefined
                          ? 'Common (No Customer)'
                          : template.customer_username
                            ? (
                              <>
                                {template.customer_username}
                                {template.customer_email && (
                                  <Typography variant="caption" display="block" sx={{ color: '#666', fontSize: '0.75rem' }}>
                                    {template.customer_email}
                                  </Typography>
                                )}
                              </>
                            )
                            : '-'
                      }
                    </TableCell>
                  )}
                  <TableCell>{template.updatedAt ? new Date(template.updatedAt).toLocaleString() : '-'}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="View Template">
                      <IconButton color="primary" onClick={() => openPreview(template)} sx={{ mr: 1 }}>
                        <FaEye />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit Template">
                      <IconButton color="primary" onClick={() => openEditTemplateModal(template)} sx={{ mr: 1 }}>
                        <FaEdit />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete template">
                      <IconButton color="error" onClick={() => handleDeleteTemplate(template.id)}>
                        <FaTrash />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Template Modal */}
      <Modal open={templateModalOpen} onClose={() => setTemplateModalOpen(false)}>
        <Box component="form" onSubmit={handleTemplateSubmit} sx={modalStyle}>
          <Typography variant="h6" mb={2}>
            {templateForm.id ? 'Edit Template' : 'Add Template'}
          </Typography>
          <Stack spacing={2}>


            <Stack direction="row" spacing={2}>
            <FormControl sx={{ flex: 1 }}>
                <InputLabel id="template-source-label">Customer</InputLabel>
                <Select
                value={
                  templateForm.customer_id !== undefined && templateForm.customer_id !== null
                    ? templateForm.customer_id
                    : ''
                }
                onChange={e => {
                  const value = e.target.value === '' ? '' : e.target.value;
                  handleTemplateFormChange('customer_id', value);
                }}
                label="Customer"
                disabled={userRole === 2} // Disable for role 2 users (only their own account; matches list.js)
              >
                {/* Admins see value 0 = "Common (No Customer)" option, others do not */}
                {/* {userRole === 1 && (
                  <MenuItem value={0}>Common (No Customer)</MenuItem>
                )} */}
                {/* Customer options */}
                {customers.length > 0 ? (
                  customers
                    .filter(c => c.id !== 0)
                    .map(c => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.username} ({c.email})
                      </MenuItem>
                    ))
                ) : (
                  <MenuItem disabled>Loading customers...</MenuItem>
                )}
              </Select>
              </FormControl>
              <TextField
                label="Template name"
                value={templateForm.name}
                onChange={(event) => handleTemplateFormChange('name', event.target.value)}
                required
                sx={{ flex: 1 }}
              />

            </Stack>

            {/* Display Custom Fields for selected customer */}
            {templateForm.customer_id && templateForm.customer_id !== 0 && customFields.length > 0 && (
              <Box sx={{ 
                p: 2, 
                bgcolor: 'info.light', 
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'info.main'
              }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Available Custom Fields (use these keys in your template):
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {customFields.map((field, index) => (
                    <Chip
                      key={index}
                      label={`${field.name} (${field.key})`}
                      size="small"
                      sx={{ 
                        bgcolor: 'background.paper',
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: 'action.hover'
                        }
                      }}
                      onClick={() => {
                        // Copy key to clipboard
                        navigator.clipboard.writeText(field.key);
                        toast.success(`Copied ${field.key} to clipboard`);
                      }}
                    />
                  ))}
                </Stack>
                <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary' }}>
                  Click on any field to copy its key to clipboard. Use these keys in your HTML template (e.g., {customFields[0]?.key || '[key]'})
                </Typography>
              </Box>
            )}

            {templateForm.sourceType === 'blocks' ? (
              <FormControl fullWidth>
                <InputLabel id="template-blocks-label">Blocks</InputLabel>
                <Select
                  labelId="template-blocks-label"
                  multiple
                  value={templateForm.selectedBlocks}
                  onChange={(event) => handleTemplateFormChange('selectedBlocks', event.target.value)}
                  renderValue={(selected) => (
                    <Stack direction="row" flexWrap="wrap" gap={1}>
                      {selected.map((value) => {
                        const block = blocks.find((item) => item.id === value);
                        return <Chip key={value} label={block?.name || value} size="small" />;
                      })}
                    </Stack>
                  )}
                >
                  {blockOptions.length === 0 && (
                    <MenuItem disabled>Add a block first</MenuItem>
                  )}
                  {blockOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography>{option.label}</Typography>
                        <Chip label={option.category} size="small" />
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <>
                <Stack direction="row" spacing={2}>
                  <Button component="label" variant="outlined" sx={{ flex: 1 }}>
                    Upload HTML file
                    <input type="file" hidden accept=".html,.htm" onChange={(event) => handleHtmlFile(event.target.files?.[0])} />
                  </Button>
                  <Button component="label" variant="outlined" sx={{ flex: 1 }}>
                    Upload Images ZIP file
                    <input 
                      type="file" 
                      hidden 
                      accept=".zip" 
                      ref={zipFileInputRef}
                      onChange={(event) => handleZipFile(event.target.files?.[0])} 
                    />
                  </Button>
                  <FormControl sx={{ flex: 1 }}>
                    <InputLabel id="editor-mode-label">Editor Mode</InputLabel>
                    <Select
                      labelId="editor-mode-label"
                      label="Editor Mode"
                      value={templateForm.editorMode || 'html'}
                      onChange={(event) => {
                        const newMode = event.target.value;
                        // Sync data when switching modes
                        if (newMode === 'ckeditor' && editorRef.current) {
                          editorRef.current.setData(templateForm.html);
                        }
                        handleTemplateFormChange('editorMode', newMode);
                      }}
                    >
                      <MenuItem value="html">HTML Editor</MenuItem>
                      <MenuItem value="ckeditor">CKEditor (WYSIWYG)</MenuItem>
                    </Select>
                  </FormControl>
                </Stack>
                {templateForm.fileName && (
                  <Typography variant="caption" color="text.secondary">
                    Loaded from {templateForm.fileName}
                  </Typography>
                )}
                {templateForm.zipFileName && (
                  <Typography variant="caption" color="text.secondary">
                    {templateForm.zipFile 
                      ? `Images loaded from ${templateForm.zipFileName} (${Object.keys(templateForm.imageMap).length} image(s))`
                      : templateForm.id 
                        ? `Previously uploaded: ${templateForm.zipFileName}. Upload a new ZIP to replace it.`
                        : `Images loaded from ${templateForm.zipFileName} (${Object.keys(templateForm.imageMap).length} image(s))`
                    }
                  </Typography>
                )}
                {templateForm.editorMode === 'html' ? (
                  <TextField
                    label="HTML Content"
                    value={templateForm.html}
                    onChange={(event) => {
                      handleTemplateFormChange('html', event.target.value);
                      // Update CKEditor if it exists
                      if (editorRef.current) {
                        editorRef.current.setData(event.target.value);
                      }
                    }}
                    multiline
                    minRows={6}
                    placeholder="Paste your HTML here... Use image paths that match files in your ZIP (e.g., image.jpg or images/logo.png)"
                  />
                ) : (
                  <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <CKEditor
                      editor={ClassicEditor}
                      data={templateForm.html}
                      onReady={handleEditorReady}
                      onChange={(event, editor) => {
                        const data = editor.getData();
                        handleTemplateFormChange('html', data);
                      }}
                      config={{
                        toolbar: [
                          'heading',
                          '|',
                          'bold',
                          'italic',
                          'link',
                          'bulletedList',
                          'numberedList',
                          '|',
                          'outdent',
                          'indent',
                          '|',
                          'blockQuote',
                          'insertTable',
                          'imageUpload',
                          '|',
                          'undo',
                          'redo'
                        ],
                        height: '400px'
                      }}
                    />
                  </Box>
                )}
                {templateForm.html && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" gutterBottom>
                      HTML Preview
                    </Typography>
                    <Box
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        p: 2,
                        bgcolor: '#ffffff',
                        minHeight: '200px',
                        maxHeight: '400px',
                        overflow: 'auto',
                      }}
                      dangerouslySetInnerHTML={{ __html: formPreviewHtml || templateForm.html }}
                    />
                  </>
                )}
              </>
            )}
             <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Conditional Block:
                </Typography>
                <Box
                  sx={{
                    bgcolor: 'background.paper',
                    border: '1px dashed',
                    borderColor: 'primary.light',
                    borderRadius: 1,

                    fontFamily: 'monospace',
                    fontSize: '0.95rem',
                    color: 'text.secondary',
                    whiteSpace: 'pre-line',
                  }}
                  component="pre"
                >
                  {`{# IF:tags=vip,premium #}
  <p>Special offer for VIP or Premium users!</p>
{# ENDIF:tags #}`}
                </Box>

              </Box>

            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button onClick={() => setTemplateModalOpen(false)}>Cancel</Button>
              <Button type="submit" variant="contained">
                {templateForm.id ? 'Update Template' : 'Save Template'}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Modal>

      {/* Preview Modal */}
      <Modal open={previewModalOpen} onClose={() => setPreviewModalOpen(false)}>
        <Box sx={{ ...modalStyle, maxWidth: 1000 }}>
          <Typography variant="h6" mb={2}>
            Template Preview - {activePreview?.name || 'Preview'}
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            This is how your email template will appear to recipients
          </Typography>
          {activePreview ? (
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 2,
                bgcolor: '#ffffff',
                maxHeight: '70vh',
                overflow: 'auto',
                minHeight: '400px',
              }}
              dangerouslySetInnerHTML={{ __html: previewHtml || activePreview.html }}
            />
          ) : (
            <Typography>No preview available</Typography>
          )}
          <Stack direction="row" justifyContent="flex-end" mt={2}>
            <Button onClick={() => setPreviewModalOpen(false)}>Close</Button>
          </Stack>
        </Box>
      </Modal>
    </Box>
  );
};

export default Template;
